import { useState } from "react";
import { Phone, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", course: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.course) {
      setError("Please fill in name, email, and select a course.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/enroll`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enroll" data-testid="enroll-section" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Info */}
        <div>
          <p className="font-mono text-xs text-[#22D3EE] uppercase tracking-widest mb-2">// get started</p>
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-4">
            Enroll Today
          </h2>
          <p className="text-base text-[#A1A1AA] leading-relaxed mb-8 max-w-lg">
            Don't miss this opportunity to advance your career. Seats are limited for personalized attention.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#16161F] border border-[#27272A] rounded-md">
                <Phone size={18} className="text-[#E32636]" />
              </div>
              <div>
                <p className="text-xs text-[#71717A]">Call us</p>
                <a href="tel:+918978266997" className="text-sm font-semibold text-[#F4F4F5]">+91-8978266997</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#16161F] border border-[#27272A] rounded-md">
                <MessageCircle size={18} className="text-[#E32636]" />
              </div>
              <div>
                <p className="text-xs text-[#71717A]">WhatsApp Group</p>
                <a
                  href="https://chat.whatsapp.com/FNEonQAoqzv3dE6VPi4JNl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-[#22D3EE] hover:text-[#67E8F9]"
                >
                  Join Now
                </a>
              </div>
            </div>
          </div>

          <div className="p-4 bg-[#16161F] border border-[#27272A] rounded-md space-y-1">
            <p className="text-sm text-[#A1A1AA]">
              <span className="font-semibold text-[#F4F4F5]">AWS Infra:</span> Mon-Thu, 5:00-6:15 PM IST · Starts 15-Jun-2026
            </p>
            <p className="text-sm text-[#A1A1AA]">
              <span className="font-semibold text-[#F4F4F5]">AWS+DB Architect:</span> Sat 8:30-10:30 AM IST · Ongoing
            </p>
            <p className="text-sm text-[#A1A1AA]">
              <span className="font-semibold text-[#F4F4F5]">Azure AZ-305:</span> Demo 10-Nov-2026 · 8:30 AM IST
            </p>
            <p className="text-sm text-[#A1A1AA]">
              <span className="font-semibold text-[#F4F4F5]">OCI Course:</span> Mon-Thu, 8:30-10:00 AM IST · Demo 30-May-2026
            </p>
            <p className="text-sm text-[#A1A1AA]">
              <span className="font-semibold text-[#F4F4F5]">Trainer:</span> Kanhiyalal Kushwaha
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-[#16161F] border border-[#27272A] rounded-md p-8">
          {submitted ? (
            <div data-testid="enrollment-success" className="text-center py-8">
              <div className="w-16 h-16 bg-[#22D3EE]/10 border border-[#22D3EE]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#22D3EE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-['Space_Grotesk'] text-xl font-bold text-[#F4F4F5] mb-2">Enrollment Received!</h3>
              <p className="text-[#A1A1AA]">We'll contact you shortly with course details.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="enrollment-form">
              <h3 className="font-['Space_Grotesk'] text-xl font-bold text-[#F4F4F5] mb-6">Register Your Interest</h3>

              {error && (
                <div data-testid="enrollment-error" className="mb-4 p-3 bg-[#E32636]/10 border border-[#E32636]/30 text-[#E32636] text-sm rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-1">Course *</label>
                  <select
                    data-testid="enroll-course-select"
                    value={form.course}
                    onChange={(e) => setForm({ ...form, course: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0A0A0F] border border-[#27272A] rounded-md text-sm text-[#F4F4F5] focus:outline-none focus:ring-2 focus:ring-[#E32636] focus:border-transparent"
                  >
                    <option value="">Select a course</option>
                    <option value="aws-infra">AWS Cloud Infrastructure</option>
                    <option value="aws-combined">AWS Cloud & Database Architect (Combined)</option>
                    <option value="azure-az305">Azure Solutions Architect (AZ-305)</option>
                    <option value="oci">Complete OCI Mastery for Oracle DBA</option>
                    <option value="multiple">Multiple Courses</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-1">Full Name *</label>
                  <input
                    data-testid="enroll-name-input"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0A0A0F] border border-[#27272A] rounded-md text-sm text-[#F4F4F5] placeholder-[#71717A] focus:outline-none focus:ring-2 focus:ring-[#E32636] focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-1">Email Address *</label>
                  <input
                    data-testid="enroll-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0A0A0F] border border-[#27272A] rounded-md text-sm text-[#F4F4F5] placeholder-[#71717A] focus:outline-none focus:ring-2 focus:ring-[#E32636] focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-1">Phone Number</label>
                  <input
                    data-testid="enroll-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-[#0A0A0F] border border-[#27272A] rounded-md text-sm text-[#F4F4F5] placeholder-[#71717A] focus:outline-none focus:ring-2 focus:ring-[#E32636] focus:border-transparent"
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="enrollment-submit-button"
                disabled={loading}
                className="mt-6 w-full bg-[#E32636] hover:bg-[#FF2D3B] text-white font-semibold py-3 rounded-md transition-all duration-200 disabled:opacity-50 glow-red"
              >
                {loading ? "Submitting..." : "Enroll Now"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
